import { Avatar, Box, Button, IconButton, Tooltip, Typography, useTheme } from '@mui/material'
import React, { useEffect, useMemo, useRef, useState } from 'react'
import { observer } from 'mobx-react'

import { debounce, Icons, USER_CARD_COLOR_SETS } from '../../utils/utilities'
import AudioVisualizer from './AudioVisualizers/AudioVisualizer'
import { useMeetingStore } from '../../store/meeting.store'
import DataLoadingSpinner from '../common/DataLoadingSpinner'
import { useAudioLevels } from '../../hooks/useAudioData'
import { useMeetingActionsContext } from '../../contexts/meetingactions.context'

function stringAvatar ( name, index, palette ) {

    return {
        sx: {
            background: USER_CARD_COLOR_SETS[index].primary,
            textTransform: "uppercase",
            color: palette.common.white,
            width: { md: "60px", sm: "40px", xs: "30px" },
            height: { md: "60px", sm: "40px", xs: "30px" },
            fontSize: { md: "initial", sm: "18px", xs: "14px" },
            zIndex: 20
        },
        children: name ? `${name.split( ' ' )?.[0]?.[0] || ""}${name.split( ' ' )?.[1]?.[0] || ""}` : "",
    }
}

const SharingCard = ( { user, gridArea, toggleScreenShare } ) => {

    const [overlayDimentions, setOverlayDimentions] = useState( 100 )
    const [mirroringOverlay, setMirroringOverlay] = useState( false )
    const [videoLoaded, setVideoLoaded] = useState( false )

    const MeetingStore = useMeetingStore()

    const { palette } = useTheme()

    const mirrorOverlayDismissed = useRef( false )
    const videoRef = useRef()
    const cardRef = useRef()

    const handleVideoLoadedMetadata = event => {
        setVideoLoaded( true )
    }

    const dismissMirroringOverlay = () => {
        mirrorOverlayDismissed.current = true
        setMirroringOverlay( false )
    }

    const userName = user.name || MeetingStore.getMeetingUsersDetails?.[user.user_id]?.name || MeetingStore.getMeetingUsersDetails?.[user.user_id]?.email || user.user_id

    const { webinarPipWindow } = useMeetingActionsContext()

    useEffect( () => {
        const cardElement = cardRef.current
        if ( !cardElement ) return

        const updateSize = ( element ) => {
            // Use the element being observed (the card)
            const rect = element.getBoundingClientRect()
            setOverlayDimentions( {
                width: rect.width,
                height: rect.height,
            } )
        }

        // 1. IMPORTANT: Set the initial size immediately
        updateSize( cardElement )

        const debouncedResizeHandler = debounce( 'presentation-card-resize-observer', ( entries ) => {
            for ( const entry of entries ) {
                // We only care about the card container
                if ( entry.target === cardRef.current ) {
                    updateSize( entry.target )
                }
            }
        }, 200 )

        const resizeObserver = new ResizeObserver( debouncedResizeHandler )
        resizeObserver.observe( cardElement )

        return () => {
            resizeObserver.disconnect()
        }
    }, [webinarPipWindow] )

    useEffect( () => {
        if ( mirrorOverlayDismissed.current || !videoLoaded ) return

        const localUserId = MeetingStore.getLocalUser?.user_id
        const isLocalSharing = user.user_id === `${localUserId}-sharing`
        const { sharingState: { surface } = {} } = MeetingStore.getMeetingState

        if ( isLocalSharing && surface === "monitor" ) {

            const timeout = setTimeout( () => {
                setMirroringOverlay( true )
            }, 1000 )

            return () => clearTimeout( timeout )
        }

        // Fallback cleanup
        setMirroringOverlay( false )
    }, [user, MeetingStore, videoLoaded] )


    useEffect( () => {
        const vTrack = user.videoTrack
        const aTrack = user.audioTrack
        const isLocal = user.user_id?.includes( MeetingStore.getLocalUser.user_id )
        const elementId = `camera-${user.user_id}`

        let retryCount = 0
        const MAX_RETRIES = 10 // Try for 1 second (10 * 100ms)
        let retryTimeout

        const playVideo = async () => {
            const targetDoc = webinarPipWindow ? webinarPipWindow.document : window.document
            const videoElement = targetDoc.getElementById( elementId )

            if ( vTrack && videoElement ) {
                console.log( "🚀 Attempting vTrack.play()..." )
                try {
                    await vTrack.stop()

                    videoElement.srcObject = null
                    videoElement.load()

                    setTimeout( async () => {
                        // await vTrack.play()
                        const remoteTrack = vTrack.getMediaStreamTrack()
                        const newStream = new MediaStream( [remoteTrack] )

                        // 1. Assign the stream
                        videoElement.srcObject = newStream

                        // 2. IMPORTANT: Mute the video element specifically
                        // This bypasses the "User Interaction" requirement for video
                        videoElement.muted = true
                        videoElement.setAttribute( "muted", "true" ) // Double insurance for some browsers
                        videoElement.playsInline = true

                        // 3. Play the video
                        videoElement.play()
                            .then( () => {
                                console.log( "✅ Video playing muted in PiP" )

                                // 4. Now handle the Audio separately
                                // We usually play audio through the SDK or a hidden audio element 
                                // that IS allowed to play because the user interacted with the main tab.
                                if ( aTrack && !isLocal ) {
                                    aTrack.play()
                                }
                            } )
                            .catch( e => {
                                console.error( "❌ Still failed even muted:", e )
                            } )
                    }, 100 )

                    setVideoLoaded( true )
                    console.log( "🚀 vTrack.play() ran successfully..." )
                } catch ( err ) {
                    console.error( err )

                }
            } else if ( vTrack && !videoElement && retryCount < MAX_RETRIES ) {
                retryCount++
                console.warn( `⏳ Element #${elementId} not found. Retry ${retryCount}/${MAX_RETRIES}...` )
                retryTimeout = setTimeout( playVideo, 100 ) // Check again in 100ms
            } else if ( vTrack && !videoElement ) {
                console.error( `❌ Failed to find element #${elementId} after ${MAX_RETRIES} attempts.` )
            }


            return () => {
                clearTimeout( retryTimeout )
                if ( vTrack ) vTrack.stop()
                if ( aTrack && !isLocal ) aTrack.stop()
                if ( videoElement ) {
                    videoElement.srcObject = null
                }
            }
        }

        // Initialize playback
        playVideo()

        // Audio usually doesn't need an element, so we play it immediately
        if ( aTrack && !isLocal ) {
            aTrack.play()
        }
    }, [user.user_id, user.videoTrack, user.audioTrack, MeetingStore, webinarPipWindow] )

    useEffect( () => {
        if ( !user.videoTrack ) return

        const handleStatusChange = ( status ) => {
            if ( status === "PLAYING" ) setVideoLoaded( true )
        }

        user.videoTrack.on( "video-status-changed", handleStatusChange )

        return () => {
            user.videoTrack.off( "video-status-changed", handleStatusChange )
        }
    }, [user.videoTrack] )

    console.log( "CARD: ", user )

    return (
        <Box
            ref={cardRef}
            bgcolor="red"
            width="100%"
            sx={{
                transition: "1s",
                gridArea,
                background: "none",
                display: "flex",
                justifyContent: "center",
                overflow: "hidden",
                position: "relative",
            }}
        >
            <Box
                component="video"
                ref={videoRef}
                onLoadedMetadata={handleVideoLoadedMetadata}
                id={`camera-${user.user_id}`}
                sx={{
                    maxWidth: "100%",
                    height: user.activePresenter ? "auto" : "100%",
                    width: user.activePresenter ? "auto" : "100%",
                    maxHeight: "100%",
                    objectFit: user.activePresenter ? "contain" : "cover",
                    display: "block",
                    borderRadius: "10px",
                    overflow: "clip"
                }}
            />
            {mirroringOverlay && <Box position="absolute" borderRadius="20px" padding="10px" height={`${overlayDimentions.height}px`} width={`${overlayDimentions.width}px`} display={{ xs: "none", md: overlayDimentions.width > 400 ? "flex" : "none" }} flexDirection="column" justifyContent="center" alignItems="center" sx={{ left: "50%", transform: `translateX(-50%)`, zIndex: 30, background: `rgba(0, 0, 0, 0.8)` }} >
                <Typography color={palette.common.white} fontSize="28px" textAlign="center" component="p" fontWeight="700"  >
                    You are presenting!
                </Typography>
                <Typography color={palette.common.white} textAlign="center" fontSize="14px" maxWidth="500px" >
                    To prevent infinity mirror, please avoid sharing your full screen or browser window. Share a different tab or window instead
                </Typography>
                <Box display="flex" gap="10px" marginTop="20px">
                    <Button color="secondary" onClick={dismissMirroringOverlay} variant='outlined' sx={{ borderRadius: "20px" }} >Show Screen Anyway</Button>
                    <Button color="secondary" onClick={toggleScreenShare} variant='outlined' sx={{ borderRadius: "20px" }} >Stop Sharing</Button>
                </Box>
            </Box>}
            {!videoLoaded && <Box position="absolute" borderRadius="20px" display="flex" padding="10px" height={`${overlayDimentions.height}px`} width={`${overlayDimentions.width}px`} flexDirection="column" justifyContent="center" alignItems="center" sx={{ left: "50%", transform: `translateX(-50%)`, zIndex: 30 }} >
                <DataLoadingSpinner waitingMessage="Starting presentation..." />
            </Box>}

            {!user.activePresenter && <Box borderRadius="20px" bgcolor={`rgba(32, 33, 36, 0.8)`} position="absolute" height={`${overlayDimentions.height}px`} padding="10px" width={`${overlayDimentions.width}px`} display="flex" alignItems="flex-end" zIndex={5}   >
                <Box display="flex" flexDirection="column" width="100%" overflow="hidden" alignItems="flex-start">
                    <Typography color="white" width="100%" noWrap fontSize={{ sm: "14px", xs: "12px" }} sx={{ textShadow: "0 1px 2px rgba(0, 0, 0, .6), 0 0 2px rgba(0, 0, 0, .3)" }} >
                        {userName}
                    </Typography>
                    <Typography color="white" noWrap fontSize="10px" sx={{ textShadow: "0 1px 2px rgba(0, 0, 0, .6), 0 0 2px rgba(0, 0, 0, .3)" }} >
                        PRESENTATION
                    </Typography>
                </Box>
            </Box>}
            <Box position="absolute" borderRadius="20px" padding="10px" height={`${overlayDimentions.height}px`} width={`${overlayDimentions.width}px`} display="flex" justifyContent="flex-end" sx={{ left: "50%", transform: `translateX(-50%)`, zIndex: 10, transition: "background 150ms", "&:hover .card-buttons": { opacity: "1", pointerEvents: "all" } }} >
                <Box borderRadius="20px" padding="2px" bgcolor={palette.common.black + "e"} display="flex" sx={{ opacity: "0", pointerEvents: "none", transition: "150ms" }} height="fit-content" className="card-buttons" width="fit-content" gap="5px" alignItems="center">
                    <Tooltip title="Presentation cards are pinned by default" >
                        <Box sx={{ cursor: "not-allowed" }} component="span">
                            <IconButton disabled sx={{ color: "white", ":disabled": { color: "GrayText" } }}  >
                                {Icons.default.PushPin}
                            </IconButton>
                        </Box>
                    </Tooltip>
                    <Tooltip disableInteractive placement='top' title="Open this feed in fullscreen">
                        <IconButton onClick={() => MeetingStore.toggleFullScreen( cardRef.current )} sx={{ color: "white", ":disabled": { color: "GrayText" } }}  >
                            {Icons.default.OpenInFull}
                        </IconButton>
                    </Tooltip>
                </Box>
            </Box>
        </Box>
    )
}

const ParticipantCard = observer( ( { user, gridArea } ) => {

    const { palette } = useTheme()

    const MeetingStore = useMeetingStore()

    const handRaiseQueuePosition = useMemo( () => MeetingStore.getRaisedHands?.findIndex( hr => hr.user_id === user.user_id ) + 1, [user, MeetingStore.getRaisedHands] )

    const userName = user.name || MeetingStore.getMeetingUsersDetails?.[user.user_id]?.name || MeetingStore.getMeetingUsersDetails?.[user.user_id]?.email || user.user_id

    const { webinarPipWindow } = useMeetingActionsContext()

    const { levelsRef } = useAudioLevels( user.audioTrack, "volumebar", user.user_id )
    const darkRingWaveRef = useRef()
    const lightRingWaveRef = useRef()

    useEffect( () => {
        const vTrack = user.videoTrack
        const aTrack = user.audioTrack
        const isLocal = user.user_id?.includes( MeetingStore.getLocalUser.user_id )
        const elementId = `camera-${user.user_id}`

        const targetDoc = webinarPipWindow ? webinarPipWindow.document : window.document
        const videoElement = targetDoc.getElementById( elementId )

        if ( vTrack ) {
            vTrack.play( videoElement )
        }

        if ( aTrack && !isLocal ) {
            aTrack.play()
        }

        return () => {
            if ( vTrack ) vTrack.stop()
            if ( aTrack && !isLocal ) aTrack.stop()
        }

    }, [user.user_id, user.videoTrack, user.audioTrack, MeetingStore, webinarPipWindow] )

    useEffect( () => {
        if ( !darkRingWaveRef.current || !lightRingWaveRef.current || ( !user.isAudioOn || user.isVideoOn ) ) {
            return
        }

        let raf
        let smoothedLevel = 0
        const LERP_FACTOR = 0.1
        const THRESHOLD = 0.4

        const showAudioWaves = () => {
            const [targetLevel] = levelsRef.current || [0]

            smoothedLevel += ( targetLevel - smoothedLevel ) * LERP_FACTOR

            if ( smoothedLevel > THRESHOLD ) {
                const darkScale = Math.min( 0.5 + smoothedLevel, 1.1 )
                const lightScale = Math.min( 0.5 + smoothedLevel, 1.9 )

                darkRingWaveRef.current.style.transform = `translate(-50%,-50%) scale(${darkScale})`
                lightRingWaveRef.current.style.transform = `translate(-50%,-50%) scale(${lightScale})`
                darkRingWaveRef.current.style.opacity = "1"
                lightRingWaveRef.current.style.opacity = "1"
            } else {
                darkRingWaveRef.current.style.transform = `translate(-50%,-50%) scale(0.5)`
                lightRingWaveRef.current.style.transform = `translate(-50%,-50%) scale(0.5)`
                darkRingWaveRef.current.style.opacity = "0"
                lightRingWaveRef.current.style.opacity = "0"
            }

            raf = requestAnimationFrame( showAudioWaves )
        }

        showAudioWaves()
        return () => cancelAnimationFrame( raf )
    }, [user.isAudioOn, user.isVideoOn, levelsRef] )


    return (
        <Box
            key={user.user_id}
            height="100%"
            width="100%"
            border={user.isVideoOn ? `5px solid ${USER_CARD_COLOR_SETS[user.color_index ?? 3].gradientColors[1]}` : `none`}
            borderRadius="20px"
            overflow="hidden"
            display="flex"
            alignItems="center"
            justifyContent="center"
            position="relative"
            sx={{
                boxShadow: user.isVideoOn ? "none" : `0px 0px 5px 1px ${USER_CARD_COLOR_SETS[user.color_index ?? 3].gradientColors[1]}`,
                transition: "1s",
                background: USER_CARD_COLOR_SETS[user.color_index ?? 3].gradient,
                gridArea: gridArea,
            }}
        >
            {/* <Box position="absolute" sx={{ inset: 0, opacity: 1 }} zIndex={1} bgcolor={USER_CARD_COLOR_SETS[user.color_index ?? 3].primary} ></Box> */}
            <Box
                // sx={{ aspectRatio: "16 / 9" }}
                width="100%"
                height="100%"
                display='flex'
                justifyContent="center"
                alignItems="center"
                position="relative"
                borderRadius="20px"
                zIndex={3}
                sx={{ background: USER_CARD_COLOR_SETS[user.color_index ?? 3].gradient }}
            >
                <Box component="video" id={`camera-${user.user_id}`} hidden={!user.isVideoOn} sx={{
                    width: '100%',
                    height: '100%',
                    objectFit: user.sharing ? "cover" : "initial",
                    maxWidth: '100%',
                    maxHeight: '100%',
                }} />
            </Box>
            <Box position="absolute" sx={{ inset: 0 }} zIndex={5} padding="10px 15px"  >
                <Box display="flex" alignItems="center">

                    {user.pinned && <Box marginRight="10px" sx={{ aspectRatio: "1 / 1", color: palette.common.font }} >
                        {Icons.default.PushPin}
                    </Box>}
                    {user.isAudioOn && <Box marginRight="10px" sx={{ aspectRatio: "1 / 1" }} >
                        <AudioVisualizer type='3barwave' audioTrack={user.audioTrack} size={25} fillerColor='white' fillerHeight={50} scale={0.6} />
                    </Box>}
                </Box>
            </Box>
            <Box position="absolute" sx={{ bottom: "10px", left: "15px", maxWidth: "90%" }} zIndex={5}   >
                <Box display="flex" width="100%" alignItems="center">
                    <Box marginRight={Boolean( handRaiseQueuePosition ) ? "10px" : "0"} width="fit-content" overflow="hidden" sx={{ transition: "350ms ease" }} maxWidth={Boolean( handRaiseQueuePosition ) ? "300px" : "1px"} >
                        {Boolean( handRaiseQueuePosition ) && <Box padding="2px 10px" display="flex" bgcolor={palette.grey[800]} gap="7px" alignItems="center" borderRadius="10px">
                            {MeetingStore.getRaisedHands.length > 1 && <Typography color={palette.common.white} variant='subtitle2' fontWeight={800} fontSize="14px" textAlign="center" display="flex" alignItems="center" justifyContent="center" borderRadius="50%" sx={{ aspectRatio: "1 / 1" }} >
                                {handRaiseQueuePosition}
                            </Typography>}
                            <Box component="span" marginLeft={MeetingStore.getRaisedHands.length > 1 && '-5px'} display="block" className='animation-tilt-shake-twice'>&#9995;</Box>
                            <Typography noWrap color="white" fontSize={{ sm: "14px", xs: "12px" }} sx={{ textShadow: "0 1px 2px rgba(0, 0, 0, .6), 0 0 2px rgba(0, 0, 0, .3)" }} >
                                {userName}
                            </Typography>
                        </Box>}
                    </Box>
                    {!Boolean( handRaiseQueuePosition ) && <Typography color="white" flexGrow={1} noWrap fontSize={{ sm: "14px", xs: "12px" }} sx={{ textShadow: "0 1px 2px rgba(0, 0, 0, .6), 0 0 2px rgba(0, 0, 0, .3)" }} >
                        {userName}
                    </Typography>}
                </Box>
            </Box>
            {!user.isVideoOn && <Box zIndex={6} position="absolute" display="flex" alignItems="center" justifyContent="center" padding="10px" sx={{ inset: 0, pointerEvents: "none" }} >
                <Avatar {...stringAvatar( userName, user.color_index ?? 3, palette )} />
                {user.isAudioOn && <Box position="absolute" bgcolor="#ffffff" ref={darkRingWaveRef} width={{ md: "60px", sm: "40px", xs: "30px" }} borderRadius="50%" height={{ md: "60px", sm: "40px", xs: "30px" }} zIndex={10} left="50%" top="50%" sx={{ transform: `translate(-50%,-50%) scale(0.9)` }} />}
                {user.isAudioOn && <Box position="absolute" ref={lightRingWaveRef} width={{ md: "60px", sm: "40px", xs: "30px" }} borderRadius="50%" height={{ md: "60px", sm: "40px", xs: "30px" }} zIndex={10} left="50%" top="50%" sx={{ background: `radial-gradient(#ffffff99,#ffffff44)`, transform: `translate(-50%,-50%) scale(0.9)` }} />}
            </Box>}
        </Box>
    )
} )

const UserCard = observer( ( { toggleScreenShare, user, gridArea } ) => {
    return ( user.sharing || user.type === "share-client" ) ?
        <SharingCard toggleScreenShare={toggleScreenShare} gridArea={gridArea} user={user} />
        :
        <ParticipantCard user={user} gridArea={gridArea} />
} )

export default UserCard
